import React, { useEffect, useState } from "react";
import { Control, FieldErrors, UseFormRegister } from "react-hook-form";
import { getDoctorsWithTotal, getNearbyZipCodes } from "../../../../util/Api/api";
import VirtualizedAsyncSelect from "./VirtualizedAsyncSelect";
import ManualInput from "./doctors.manualInput";
import { Doctor, IFieldDropdown } from "./SelectDropdown.types";
import Loader from "../../../../components/Loader";

const DoctorsDropdown = (props: { fieldData: IFieldDropdown; register: UseFormRegister<any>, errors: FieldErrors; control: Control }) => {
  const {
    fieldData,
    fieldData: {
      speciality,
      doctorsLoadingText,
      kranusNetworkOnly,
      enableZipcodeSearch,
      zipcodePlaceholder,
      zipcodeResultsDistance,
      manualDoctorInput,
    },
    register,
    errors,
    control,
  } = props;

  const [data, setData] = useState(fieldData);
  const [zipSearchVisible, setZipSearchVisible] = useState(enableZipcodeSearch ?? false);
  const [zipCodeInput, setZipCodeInput] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const [isManualInput, setIsManualInput] = useState(false);

  const handleChildStateChange = (newState: boolean) => {
    setIsManualInput(newState);
  };

  useEffect(() => {
    if (!zipSearchVisible) {
      loadAllDoctors();
    }
  }, [zipSearchVisible]);

  useEffect(() => {
    if (enableZipcodeSearch && zipCodeInput) {
      fetchDoctorsForNearbyZipCodes(zipCodeInput, zipcodeResultsDistance);
    }
  }, [zipCodeInput, enableZipcodeSearch, zipcodeResultsDistance]);

  useEffect(() => {
    setZipSearchVisible(enableZipcodeSearch ? true : false);
  }, [enableZipcodeSearch]);

  const validateZipCode = (input: string) => /^\d{5}$/.test(input);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.length <= 5 && /^\d*$/.test(value)) {
      setInputValue(value);
      if (validateZipCode(value)) {
        setZipCodeInput(value);
      } else {
        setZipCodeInput(null);
      }
    }
  };

  const fetchDoctorsForAllSpecialities = async (specialties: string, zipCodes?: string[]) => {
    let allDoctors: Doctor[] = [];
    const specialtiesArray = specialties.split(",").map((s) => s.trim());

    for (const speciality of specialtiesArray) {
      for (const zip of zipCodes ?? [null]) {
        let filter: any = {};
        if (speciality) filter.specialty = speciality;
        if (zip) filter.zipcode = zip;

        const chunk = await loadAllDoctorsInChunks(filter);
        allDoctors = [...allDoctors, ...chunk];
      }
    }

    getDoctorOptions(allDoctors);
  };

  const fetchDoctorsForNearbyZipCodes = async (zipCode: string, distance: number) => {
    try {
      const nearbyZipCodes = await getNearbyZipCodes(zipCode, distance);
      if (Array.isArray(nearbyZipCodes)) {
        if (speciality) {
          await fetchDoctorsForAllSpecialities(speciality, nearbyZipCodes);
        } else {
          await fetchDoctorsForAllZipCodes(nearbyZipCodes);
        }
      } else {
        console.warn("Unexpected format for nearby zip codes response: ", nearbyZipCodes);
      }
    } catch (error) {
      console.error("Error fetching nearby zip codes or doctors: ", error);
    }
  };

  const fetchDoctorsForAllZipCodes = async (zipCodes: string[]) => {
    let allDoctors: Doctor[] = [];
    for (const zip of zipCodes) {
      let filter: any = {};
      if (speciality) filter.specialty = speciality;
      filter.zipcode = zip;

      const chunk = await loadAllDoctorsInChunks(filter);
      allDoctors = [...allDoctors, ...chunk];
    }
    getDoctorOptions(allDoctors);
  };

  const loadAllDoctors = async () => {
    if (!speciality) {
      let filter: any = {};
      if (kranusNetworkOnly) filter.kranus_network = true;
      const allDoctors = await loadAllDoctorsInChunks(filter);
      getDoctorOptions(allDoctors);
      return;
    }

    await fetchDoctorsForAllSpecialities(speciality);
  };

  const loadAllDoctorsInChunks = async (filter: any) => {
    let allDoctors: Doctor[] = [];
    const limit = 1000;
    let skip = 0;

    while (true) {
      const { items: doctorsChunk, total } = await getDoctorsWithTotal(filter, limit, skip);

      if (doctorsChunk.length === 0) break;

      allDoctors = [...allDoctors, ...doctorsChunk];

      if (skip + limit >= total) {
        break;
      }

      skip += limit;
    }

    return allDoctors;
  };

  const getDoctorOptions = (doctorsData: Doctor[]) => {
    const uniqueDoctorsMap = new Map<string, Doctor>();
    for (const doctor of doctorsData) {
      if (doctor?.id_) {
        uniqueDoctorsMap.set(doctor.id_, doctor);
      } else {
        const fallbackKey = `${doctor?.full_name}-${doctor?.address}`;
        if (!uniqueDoctorsMap.has(fallbackKey)) {
          uniqueDoctorsMap.set(fallbackKey, doctor);
        }
      }
    }

    const uniqueDoctors = Array.from(uniqueDoctorsMap.values());
    const options = uniqueDoctors.map((doctor) => {
      const doctorLabel = `<b>${doctor?.full_name}</b> - ${doctor?.address}`;
      return {
        label: doctorLabel,
        value: doctor,
      };
    });

    setData({ ...fieldData, options });
  };

  const DoctorsLoading = (
    <div style={{ width: "100%", textAlign: "center", fontWeight: "bold", fontStyle: "italic", color: "#666" }}>
      <span dangerouslySetInnerHTML={{ __html: doctorsLoadingText }} />
    </div>
  );

  return (
    <>
      {zipSearchVisible ? (
        <input
          type="text"
          value={inputValue}
          onChange={handleChange}
          placeholder={zipcodePlaceholder}
          style={{
            border: "1px solid #d4d4d4",
            borderRadius: "6px",
            marginBottom: "20px",
            boxSizing: "border-box",
            color: "rgba(0, 0, 0, 0.8)",
            padding: "18px 20px",
            width: "100%",
            fontSize: "16px",
            backgroundColor: "rgb(255, 255, 255)",
            borderColor: "rgb(191, 191, 191)",
            borderWidth: "1px",
            boxShadow: "rgba(0, 0, 0, 0.25) 0px 2px 4px 0px inset",
          }}
        />
      ) : null}

      {data.options ? (
        <VirtualizedAsyncSelect
          fieldData={data}
          isManualInput={isManualInput}
          onStateChangeManualInput={handleChildStateChange}
          errors={errors}
          control={control} />
      ) : (
        (enableZipcodeSearch && zipCodeInput) ? <Loader /> : DoctorsLoading
      )}

      {manualDoctorInput && 
        <ManualInput
          manualDoctorInput={manualDoctorInput}
          isManualInput={isManualInput}
          onStateChangeManualInput={handleChildStateChange}
          register={register}
          errors={errors} 
        />}
    </>
  );
};

export default DoctorsDropdown;
